import React, { useEffect, useState, useRef } from "react";
import PieChart from "../../../components/PieChart";
import DateBarChart from "../../../components/chart/DateBarChart";
import ChartTable from "../../../components/chart/ChartTable";
import AlertBarChart from "../../../components/chart/AlertBarChart";
import { knf_logo } from "../../../assets/imgs";
import "./ConsolidatedReport.css";
import html2pdf from "html2pdf.js";
import moment from "moment/moment";

const chunkArray = (arr = [], size = 15) => {
  const chunks = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
};

const ConsolidatedReport = ({ data }) => {
  const pdfRef = useRef();
  const [classifications, setClassification] = useState([]);
  const [barChartDate, setBarChartDate] = useState([]);
  const [barChartNewAlerts, setBarChartNewAlerts] = useState([]);
  const [barChartClosedAlerts, setBarChartClosedAlerts] = useState([]);
  const [pieAlert, setPieAlert] = useState([]);

  useEffect(() => {
    if (!data) return;
    setClassification([
      data.total_alerts_count || 0,
      data.new_alerts_count || 0,
      data.assigned_alerts_count || 0,
      data.closed_alerts_count || 0,
    ]);
    setBarChartDate(data.graph_data?.map((i) => i.date) || []);
    setBarChartNewAlerts(data.graph_data?.map((i) => i.new_alerts_count) || []);
    setBarChartClosedAlerts(
      data.graph_data?.map((i) => i.closed_alerts_count) || [],
    );
    setPieAlert(data.pie_chart_data?.map((i) => i.percentage) || []);
  }, [data]);

  const downloadPDF = async () => {
    await new Promise((r) => setTimeout(r, 1500));
    const element = pdfRef.current;
    const opt = {
      margin: 0,
      filename: `Consolidated-Report-${moment(data?.from_date).format(
        "DD-MM-YYYY",
      )}-${moment(data?.to_date).format("DD-MM-YYYY")}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, letterRendering: true },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      pagebreak: { mode: "avoid-all" },
    };

    html2pdf()
      .set(opt)
      .from(element)
      .toPdf()
      .get("pdf")
      .then((pdf) => {
        const totalPages = pdf.internal.getNumberOfPages();
        const w = pdf.internal.pageSize.getWidth();
        const h = pdf.internal.pageSize.getHeight();
        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);
          pdf.setFontSize(9);
          pdf.setTextColor(150);
          pdf.text(`Page ${i} of ${totalPages}`, w / 2, h - 10, {
            align: "center",
          });
        }
      })
      .save();
  };

  const alertTypeChunks = chunkArray(data?.alert_type_counts || [], 15);

  console.log("data--->", data);

  let { company, site } = data ?? {};

  return (
    <div>
      <div className="download-wrapper">
        <button onClick={downloadPDF} className="submit-btn">
          Download Report
        </button>
      </div>

      <div ref={pdfRef} className="pdf-container">
        {/* PAGE 1: SUMMARY & CLASSIFICATIONS STACKED */}
        <div className="pdf-page">
          <div className="pdf-header">
            <img src={data?.company_logo} alt="KNF" className="pdf-logo" />
            <h3>
              <span style={{ color: "#852e59" }}>{company}</span> Consolidated
              Report for <span style={{ color: "#852e59" }}>{site}</span>
            </h3>
          </div>

          <table className="info-table">
            <thead>
              <tr>
                <th>Company Name</th>
                <th>Site Name</th>
                {data?.camera_name && <th>Camera Name</th>}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{data?.company}</td>
                <td>{data?.site}</td>
                {data?.camera_name && <td>{data?.camera_name}</td>}
              </tr>
            </tbody>
          </table>

          <div className="date-row">
            <div>From: {moment(data?.from_date).format("DD-MM-YYYY")}</div>
            <div>To: {moment(data?.to_date).format("DD-MM-YYYY")}</div>
          </div>

          <div className="section">
            <h3>ALERT CLASSIFICATIONS</h3>
            <hr />
            {/* Box and Graph stacked vertically */}
            <div className="pdf-box full-width mb-20">
              <div>Total Alerts: {data?.total_alerts_count}</div>
              <div>New Alerts: {data?.new_alerts_count}</div>
              <div>Assigned Alerts: {data?.assigned_alerts_count}</div>
              <div>Closed Alerts: {data?.closed_alerts_count}</div>
            </div>
            <div className="pdf-chart-medium">
              <AlertBarChart classifications={classifications} />
            </div>
          </div>
        </div>

        {/* PAGE 2: ALERT TYPES & STATISTICS (Centered) */}
        <div className="pdf-page page-break-top centered-content">
          <div className="full-width">
            <div className="section">
              <h3>ALERT TYPES</h3>
              <hr />
              {alertTypeChunks.map((chunk, index) => (
                <div key={index} className="alert-types-wrapper">
                  <ChartTable alertTypeData={chunk} />
                </div>
              ))}
            </div>

            <div className="section mt-50">
              <h3>ALERT STATISTICS</h3>
              <hr />
              <div className="pdf-box centered-box">
                <div className="stat-row">
                  Avg Alerts / Total Days:{" "}
                  <strong>{data?.average_alerts_per_day}</strong>
                </div>
                <div className="stat-row">
                  Invalid or Compliant / Total Days:{" "}
                  <strong>
                    {data?.average_invalid_compliant_alerts_per_day}
                  </strong>
                </div>
                <div className="stat-row">
                  Non-Compliant / Total Days:{" "}
                  <strong>{data?.average_non_compliant_alerts_per_day}</strong>
                </div>
                <div className="stat-row">
                  Closed / Total Days:{" "}
                  <strong>{data?.average_closed_alerts_per_day}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* PAGE 3: ALERT PER DAY & PIE CHART (Centered) */}
        <div className="pdf-page page-break-top centered-content">
          <div className="full-width">
            <div className="section">
              <h3>ALERT PER DAY</h3>
              <hr />
              <div className="pdf-chart-large">
                <DateBarChart
                  barChartDate={barChartDate}
                  barChartClosedAlerts={barChartClosedAlerts}
                  barChartNewAlerts={barChartNewAlerts}
                />
              </div>
            </div>

            <div className="section mt-60">
              <h3>ALERT CLASSIFICATIONS (DISTRIBUTION)</h3>
              <p className="small mt-1">
                Note: (New, Assigned, Closed Alerts) / Total Alerts * 100
              </p>
              <hr />
              <div
                className="pie-chart-container"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  minHeight: "400px",
                  marginTop: "20px",
                  marginBottom: "20px",
                }}
              >
                <div
                  style={{
                    width: "350px",
                    height: "350px",
                    position: "relative",
                  }}
                >
                  <PieChart pieAlert={pieAlert} />
                </div>
              </div>
            </div>
          </div>
          <div
            className="d-flex align-items-center justify-content-center gap-2 flex-wrap"
            style={{
              padding: "10px 16px",
              borderRadius: "12px",
              background: "#fff8fc",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              width: "fit-content",
              margin: "auto",
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: "13px",
                fontWeight: 500,
                color: "#666",
              }}
            >
              Powered By
            </p>

            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: "50%",
                overflow: "hidden",
                background: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
              }}
            >
              <img
                src={knf_logo}
                alt="KNF Logo"
                style={{
                  width: "80%",
                  height: "80%",
                  objectFit: "contain",
                }}
              />
            </div>

            <p
              style={{
                margin: 0,
                fontSize: "13px",
                fontWeight: 500,
                letterSpacing: "0.5px",
                color: "#852e59",
              }}
            >
              KNF ENTERPRISES
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsolidatedReport;
